import type { TemplateResult } from 'lit'
import { html, LitElement, nothing } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { ref } from 'lit/directives/ref.js'
import { SlotController } from '../common/controllers/SlotController.js'
import { cond } from '../common/directives/cond.js'
import { FocusableMixin } from '../common/mixins/FocusableMixin.js'

import componentStyle from '../common/styles/Component.css'
import style from './DropdownItem.css'

/**
 * Dropdown item populates dropdown with actions. Items can be
 * placed either inside a dropdown group or directly inside a
 * dropdown component.
 *
 * @status ready
 * @category overlay
 * @slot - The dropdown item content.
 * @slot start - Used to place content before dropdown item text. Typically used for icons.
 * @slot end - Used to place content after dropdown item text. Typically used for icons.
 *
 * @cssprop [--n-dropdown-item-background-color=transparent] - Controls the background color of the item, using our [color tokens](/tokens/#color).
 * @cssprop [--n-dropdown-item-color=var(--n-color-text)] - Controls the color of the text within the item, using our [color tokens](/tokens/#color).
 * @cssprop [--n-dropdown-item-white-space=nowrap] - Controls how the item's label handles whitespace. Set to `normal` to allow long labels to wrap across multiple lines instead of truncating with an ellipsis.
 * @cssprop [--n-dropdown-item-align-items=center] - Controls how the item's contents (start slot, label, end slot) align on the cross axis. Useful with multi-line wrapping: set to `flex-start` so icons/avatars sit on the first line instead of the vertical middle.
 *
 * @parent Dropdown
 */
@customElement('nord-dropdown-item')
export default class DropdownItem extends FocusableMixin(LitElement) {
  static styles = [componentStyle, style]

  private endSlot = new SlotController(this, 'end')

  /**
   * The url the dropdown item should link to.
   */
  @property({ reflect: true }) href?: string

  /**
   * When provided together with a href property, determines where
   * to open the linked URL. The keywords have special meanings for
   * where to load the URL: “_self” means the current browsing context,
   * “_blank” usually a new tab but users can configure browsers this to
   * open a new window instead, “_parent” means the parent browsing
   * context of the current one, but if no parent exists, behaves as
   * _self, and finally “top” means the topmost browsing context.
   */
  @property() target: '_self' | '_blank' | '_parent' | '_top' = '_self'

  /**
   * Makes the component disabled. This prevents users from
   * being able to interact with the component, and conveys
   * its inactive state to assistive technologies.
   */
  @property({ type: Boolean, reflect: true }) disabled = false

  render() {
    const innards = html`
      <slot name="start"></slot>
      <div class="n-content"><slot></slot></div>
      <slot name="end" ?hidden=${this.endSlot.isEmpty}></slot>
    `

    return this.href ? this.renderLink(innards) : this.renderButton(innards)
  }

  /**
   * We jump through some hoops here to ensure the link is treated correctly when "disabled".
   * Links cannot be disabled natively, so we need to rely on some aria magic to get the correct semantics.
   * Along with the advice in the article below, we also set tabindex to "-1", so it is taken out of tab order.
   *
   * @see https://www.scottohara.me/blog/2021/05/28/disabled-links.html
   */
  private renderLink(innards: TemplateResult) {
    return html`
      <a
        ${ref(this.focusableRef)}
        class="n-dropdown-item"
        target=${this.target}
        href=${cond(this.disabled, nothing, this.href)}
        tabindex=${cond(this.disabled, '-1')}
        aria-disabled=${cond(this.disabled, 'true')}
        role=${cond(this.disabled, 'link')}
        >${innards}
      </a>
    `
  }

  private renderButton(innards: TemplateResult) {
    return html`
      <button ${ref(this.focusableRef)} class="n-dropdown-item" ?disabled=${this.disabled}>${innards}</button>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-dropdown-item': DropdownItem
  }
}
